import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { UserDTO } from '@/data/dto/user.dto';
import RouterUtil from '@/utils/router.util';
import { RouteConfig } from 'vue-router';
import router from '@/router';
import { SessionDTO } from '@/data/dto/session.dto';
import { CredentialsDTO } from '@/data/dto/credentials.dto';
import MOMABffService from '@/services/moma-bff.service';
import { FindUserFilter } from '@/data/dto/research-filter.dto';

@Module
export default class UserModule extends VuexModule {
  bffService = new MOMABffService();
  isLogged = !!window.localStorage.getItem('token');
  isSimulatingUser = !!window.localStorage.getItem('simulated-token');
  currentUser?: UserDTO = undefined;
  userRoutes: RouteConfig[] = [];

  @Action
  async login(credentials: CredentialsDTO): Promise<void> {
    await this.context.dispatch('logout');
    const session = await this.bffService.login(credentials);
    this.context.commit('loginSuccess', session);
  }

  @Action
  async authorize() {
    return this.bffService
      .authorize()
      .then((user) => {
        this.context.commit('setCurrentUser', user);
        this.context.commit('setUserRoutes', {
          user,
          routes: router.options.routes,
        });
      })
      .catch(() => {
        this.context.dispatch('logout');
      });
  }

  @Action
  async getAllUsers(filters?: FindUserFilter) {
    return this.bffService.getAllUsers(filters).then((users) => users);
  }

  @Action
  async getAllUsersToExport(filters?: FindUserFilter) {
    return this.bffService.getAllUsersToExport(filters).then((users) => users);
  }

  @Action
  getOneUser(email: string) {
    return this.bffService.getOneUser(email);
  }

  @Action
  createUser(user: any) {
    return this.bffService.createUser(user.email, user.name, user.groups);
  }

  @Action
  updateUserStatus(user: any) {
    return this.bffService.updateUserStatus(user.email, user.active);
  }

  @Action
  updateUserName(user: any) {
    return this.bffService.updateUserName(user.email, user.name);
  }

  @Action
  deleteUser(user: any) {
    return this.bffService.deleteUser(user.email);
  }

  @Action
  addUserToApplicationGroup(payload: any) {
    return this.bffService.addUserToApplicationGroup(
      payload.email,
      payload.groups
    );
  }

  @Action
  removeUserFromApplicationGroup(payload: any) {
    return this.bffService.removeUserFromApplicationGroup(
      payload.email,
      payload.groupUUID
    );
  }

  @Action
  logout(): void {
    this.context.commit('clearSession');
    this.context.commit('clearCurrentUser');
    this.context.commit('clearUserRoutes');
  }

  @Action
  async simulateUser(email: string): Promise<void> {
    const { token } = await this.bffService.simulateUser(email);
    this.context.commit('setSimulatedUserSession', token);
  }

  @Action
  async exportUsers(filters?: FindUserFilter): Promise<void> {
    const fileData = await this.bffService.exportUsers(filters);

    const filename = 'usuarios-monitor-de-marcas.xlsx';
    const url = window.URL.createObjectURL(fileData);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  @Action
  logoutSimulatedUser(): void {
    this.context.commit('removeSimulatedUserSession');
    this.context.commit('clearCurrentUser');
    this.context.commit('clearUserRoutes');
  }

  @Action
  addDashboardRoutesToCurrentUser(routes: RouteConfig[]): void {
    if (routes.length === 0) return;

    const updatedRoutes = [...this.userRoutes];

    routes.forEach((route) => {
      if (!route.meta && !route) return;
      const parentId = route.meta?.parentId;

      if (!parentId) {
        // Adiciona como rota raiz
        const existsRootWithThisName = updatedRoutes.some(
          (existingRoute: any) => existingRoute.name === route.name
        );

        if (!existsRootWithThisName) {
          updatedRoutes.push(route);
        }
      } else {
        // Adiciona como rota filha
        const parentRoute = RouterUtil.getRouteById(parentId, updatedRoutes);
        if (!parentRoute?.children) return;

        const existsChildWithThisName = parentRoute.children.some(
          (child: any) => child.name === route.name
        );

        if (!existsChildWithThisName) {
          parentRoute.children.push(route);
        }
      }
    });
    this.context.commit('setUserRoutes', {
      user: this.currentUser!,
      routes: updatedRoutes,
    });
  }

  @Action
  async syncCentralUsersData() {
   return await this.bffService.syncAllUsersFromCentral();
  }
  @Mutation
  setCurrentUser(user: UserDTO) {
    this.currentUser = user;
  }

  @Mutation
  clearCurrentUser() {
    this.currentUser = undefined;
  }

  @Mutation
  setUserRoutes(payload: { user: UserDTO; routes: RouteConfig[] }) {
    this.userRoutes = RouterUtil.identifyUserAllowedRoutes(
      payload.user,
      payload.routes
    );
  }

  @Mutation
  clearUserRoutes() {
    this.userRoutes = [];
  }

  @Mutation
  loginSuccess(session: SessionDTO) {
    window.localStorage.setItem('token', session.token);
    window.localStorage.setItem('refreshToken', session.refreshToken);
    this.isLogged = true;
  }

  @Mutation
  clearSession() {
    window.localStorage.removeItem('token');
    window.localStorage.removeItem('simulated-token');
    window.localStorage.removeItem('refreshToken');
    this.isLogged = false;
  }

  @Mutation
  setSimulatedUserSession(simulatedUserToken: string): void {
    window.localStorage.setItem('simulated-token', simulatedUserToken);
    this.isSimulatingUser = true;
  }

  @Mutation
  removeSimulatedUserSession(): void {
    window.localStorage.removeItem('simulated-token');
    this.isSimulatingUser = false;
  }
}
